import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { useUserStore } from "../../../store/userStore";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { useSupportChatStore, type ChatParticipant, type ConversationType } from "../../../store/supportChatStore";
import { useTripsStore, type Trip } from "../../../store/admin/tripsStore";
import { useUsersStore } from "../../../store/admin/usersStore";
import { ADMIN_RESELLER_ROLE_ID, useIsReseller } from "../../../hooks/useIsReseller";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faUser,
  faUsers,
  faSearch,
  faTruck,
  faSpinner,
  faRoute,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";

type ModalMode = "one-to-one" | "group" | "viaje";

interface NewConversationModalProps {
  onClose: () => void;
  onCreated: (conversationId: string) => void;
  /** Modo inicial del modal. Si se omite, arranca en "one-to-one". */
  initialMode?: ModalMode;
  /**
   * Id del viaje a preseleccionar cuando el modal abre en modo "viaje".
   * Si el trip todavía no está en `useTripsStore.trips`, se hace fetch.
   */
  initialTripId?: string;
  /**
   * Correos a preseleccionar como participantes en cuanto los usuarios de
   * la compañía terminen de cargar. Pensado para chats de viaje: el caller
   * pasa los e-mails de los conductores asignados a las unidades del viaje
   * (ver `collectTripDriverEmails`) y el modal hace el match contra el
   * directorio de la empresa. El match es case-insensitive y se ignoran
   * los que no aparezcan en la lista filtrada (p.ej. drivers de otra
   * compañía cuando no se es reseller).
   */
  initialParticipantEmails?: string[];
  /**
   * Igual que `initialParticipantEmails`, pero usando `user.id` directamente.
   * Es la ruta preferida cuando se cuenta con el dato: viene del backend
   * (`assignment.driver.user_id`, ver `tripsController._resolveDriversForVehicleIds`)
   * y evita los falsos negativos del match por email — un driver con email
   * sin sincronizar entre `drivers/{id}.email` y `users/{userId}.email`, o
   * un usuario sin email — que sí ocurren en producción.
   *
   * Ambos props son aditivos: el modal preselecciona la unión de todos los
   * matches (por id y por email) deduplicada por `user.id`.
   */
  initialParticipantUserIds?: string[];
}

export default function NewConversationModal({
  onClose,
  onCreated,
  initialMode,
  initialTripId,
  initialParticipantEmails,
  initialParticipantUserIds,
}: NewConversationModalProps) {
  const { t } = useTranslation("support");
  const { isDarkMode } = useTheme();
  const user = useUserStore((state) => state.user);
  const companyId = useCompaniesStore((state) => state.companyId);
  const { isReseller } = useIsReseller();
  const createConversation = useSupportChatStore((state) => state.createConversation);
  // Conversaciones actuales para detectar duplicados de chat directo. Cuando
  // el modal está en modo "one-to-one" usamos esta info para que los usuarios
  // con los que el current user ya tiene un chat individual no se puedan
  // volver a seleccionar (caso contrario crearíamos un chat duplicado).
  const conversations = useSupportChatStore((state) => state.conversations);
  const { trips, fetchTrips, fetchTrip, loading: isLoadingTrips } = useTripsStore();
  const { users: companyUsers, fetchUsers, loading: isLoadingUsers } = useUsersStore();

  const [mode, setMode] = useState<ModalMode>(initialMode ?? "one-to-one");
  const type: ConversationType = mode === "one-to-one" ? "one-to-one" : "group";
  const [groupName, setGroupName] = useState("");
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [isLoadingInitialTrip, setIsLoadingInitialTrip] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<ChatParticipant[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  // Fetch users on mount. Decidimos el alcance según el rol del usuario:
  //  - Reseller   → `allCompanies: true`  → trae usuarios de TODAS las
  //                 compañías (un reseller chatea con cualquier carrier).
  //  - Carrier    → `includeResellers: true` → trae los usuarios de su
  //                 propia compañía MÁS los que tengan rol "admin reseller".
  //                 Es la alternativa segura a `allCompanies` para roles
  //                 no-reseller, que tienen prohibido ver usuarios de otros
  //                 tenants.
  useEffect(() => {
    fetchUsers(
      isReseller
        ? { limit: 500, page: 1, allCompanies: true }
        : { limit: 500, page: 1, includeResellers: true },
    );
  }, [fetchUsers, isReseller]);

  // Fetch trips when Viaje mode is activated
  useEffect(() => {
    if (mode === "viaje") {
      fetchTrips({ limit: 100 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Preselect a trip when caller provides initialTripId. We try the local
  // list first (already fetched above) and fall back to a direct fetch by id
  // so the modal works even if the trip isn't in the current page of /tripsList.
  useEffect(() => {
    if (mode !== "viaje" || !initialTripId) return;
    if (selectedTrip?.id === initialTripId) return;

    const fromList = trips.find((tr) => tr.id === initialTripId);
    if (fromList) {
      setSelectedTrip(fromList);
      return;
    }

    let cancelled = false;
    setIsLoadingInitialTrip(true);
    fetchTrip(initialTripId)
      .then((result) => {
        if (cancelled) return;
        if (result.success && result.data) {
          setSelectedTrip(result.data);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingInitialTrip(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, initialTripId, trips]);

  // Debounce the search input so we don't re-run the user filter on every
  // keystroke when typing fast (matters for large company directories).
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 180);
    return () => clearTimeout(id);
  }, [searchQuery]);

  const filteredTrips = useMemo(() => {
    const base = trips.filter((trip) => trip.company_id === companyId);
    // Si el caller nos pasó un viaje preseleccionado y no aparece en el
    // listado paginado, lo inyectamos para que el <select> pueda mostrarlo
    // como opción seleccionada (caso típico: viaje viejo / fuera de la
    // primera página).
    if (selectedTrip && !base.some((t) => t.id === selectedTrip.id)) {
      return [selectedTrip, ...base];
    }
    return base;
  }, [trips, companyId, selectedTrip]);

  // UIDs de usuarios con los que el current user YA tiene un chat directo
  // (`type === "one-to-one"`). Lo usamos para bloquear el botón en la lista
  // y evitar duplicar conversaciones cuando estamos en modo "one-to-one".
  // Cubre el caso de un chat directo en cualquier dirección (yo→otro u
  // otro→yo) porque ambos quedan con los mismos participants ids.
  const existingDirectChatUserIds = useMemo(() => {
    const result = new Set<string>();
    if (!user?.id) return result;
    for (const conv of conversations) {
      if (conv.type !== "one-to-one") continue;
      // Buscamos al otro participante. Si por alguna razón el chat solo
      // tiene un participant (legacy o data corrupta) lo ignoramos.
      const other = conv.participants.find((p) => p.uid !== user.id);
      if (other) result.add(other.uid);
    }
    return result;
  }, [conversations, user?.id]);

  // Map users to ChatParticipant once per change in companyUsers/currentUser.
  // Visibilidad según rol:
  //  - Reseller → ve TODOS los usuarios devueltos por el backend (que ya
  //    viene con scope global gracias a `allCompanies: true`).
  //  - Carrier  → ve los usuarios de la compañía activa + los reseller
  //    (los resellers viven en otra compañía pero deben poder iniciar y
  //    recibir chats con cualquier carrier).
  const availableUsers = useMemo<ChatParticipant[]>(
    () =>
      companyUsers
        .filter((u) => u.id !== user?.id)
        .filter((u) =>
          isReseller
            ? true
            : u.company_id === companyId ||
              u.role_id === ADMIN_RESELLER_ROLE_ID,
        )
        .map((u) => ({
          uid: u.id,
          display_name:
            `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
            u.username ||
            u.email,
          email: u.email,
        })),
    [companyUsers, user?.id, companyId, isReseller],
  );

  const filteredUsers = useMemo(() => {
    if (debouncedSearch.length < 2) return availableUsers;
    const needle = debouncedSearch.toLowerCase();
    return availableUsers.filter(
      (u) =>
        u.display_name.toLowerCase().includes(needle) ||
        (u.email && u.email.toLowerCase().includes(needle)),
    );
  }, [availableUsers, debouncedSearch]);

  // Representa al usuario actual como "host" del chat. No se agrega al state
  // `selectedUsers` (lo añade `handleCreate` al participants[] al crear),
  // pero sí se muestra como una chip fija para que al crear un grupo/viaje
  // quede claro que ya está dentro de la conversación.
  const hostParticipant = useMemo<ChatParticipant | null>(() => {
    if (!user) return null;
    const display_name =
      `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
      user.username ||
      user.email ||
      "Usuario";
    return {
      uid: user.id,
      display_name,
      email: user.email,
    };
  }, [user]);

  const isGroupLike = mode === "group" || mode === "viaje";

  // Auto-preselección. Dos vías que se complementan:
  //
  //   - `initialParticipantUserIds`: vía preferida. Vienen del backend
  //     (`assignment.driver.user_id`). Match exacto contra `user.id`, así
  //     que es robusto incluso si el e-mail del driver no está sincronizado
  //     entre `drivers/{id}.email` y `users/{userId}.email`.
  //
  //   - `initialParticipantEmails`: fallback para callers que solo tienen
  //     el e-mail (p.ej. el realtime `vehiclesSocketStore.driver.email`,
  //     que no expone `user_id`). Match case-insensitive contra `u.email`.
  //
  // Lo disparamos cuando:
  //   1. Hay al menos un seed (por id o por email).
  //   2. El directorio de la compañía terminó de cargar (`availableUsers`
  //      no vacío).
  //
  // Solo agregamos los que aún no estén en `selectedUsers` para que el
  // usuario pueda deseleccionar uno y no se vuelva a meter en cada render.
  // Tampoco los reseteamos al cambiar de modo: si el caller los pasó es
  // porque los quiere, sin importar si se queda en "viaje" o cambia a "group".
  const seedEmailsKey = useMemo(
    () =>
      (initialParticipantEmails || [])
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
        .sort()
        .join("|"),
    [initialParticipantEmails],
  );
  const seedUserIdsKey = useMemo(
    () =>
      (initialParticipantUserIds || [])
        .map((id) => id.trim())
        .filter(Boolean)
        .sort()
        .join("|"),
    [initialParticipantUserIds],
  );

  useEffect(() => {
    if (!seedEmailsKey && !seedUserIdsKey) return;
    if (availableUsers.length === 0) return;

    const wantedEmails = seedEmailsKey
      ? new Set(seedEmailsKey.split("|"))
      : null;
    const wantedIds = seedUserIdsKey
      ? new Set(seedUserIdsKey.split("|"))
      : null;

    const matches = availableUsers.filter((u) => {
      if (wantedIds && wantedIds.has(u.uid)) return true;
      if (
        wantedEmails &&
        u.email &&
        wantedEmails.has(u.email.trim().toLowerCase())
      ) {
        return true;
      }
      return false;
    });
    if (matches.length === 0) return;

    setSelectedUsers((prev) => {
      const existing = new Set(prev.map((p) => p.uid));
      const additions = matches.filter((m) => !existing.has(m.uid));
      if (additions.length === 0) return prev;
      return [...prev, ...additions];
    });
  }, [seedEmailsKey, seedUserIdsKey, availableUsers]);

  const toggleUser = (participant: ChatParticipant) => {
    // En modo directo nunca dejamos seleccionar a alguien con quien ya existe
    // un chat 1:1 — la conversación duplicada quedaría huérfana en el sidebar
    // y confundiría al usuario. El render además marca este usuario como
    // disabled, pero también validamos aquí por si llegara por teclado.
    if (mode === "one-to-one" && existingDirectChatUserIds.has(participant.uid)) {
      return;
    }
    setSelectedUsers((prev) => {
      const exists = prev.find((p) => p.uid === participant.uid);
      if (exists) return prev.filter((p) => p.uid !== participant.uid);
      if (mode === "one-to-one") return [participant];
      return [...prev, participant];
    });
  };

  const handleCreate = async () => {
    if (selectedUsers.length === 0 || !user || !companyId) return;

    setIsCreating(true);
    try {
      const currentParticipant: ChatParticipant = {
        uid: user.id,
        display_name: `${user.first_name} ${user.last_name}`.trim() || user.username,
        email: user.email,
        // Persistimos el company_id del creador para que la UI de detalles
        // pueda mostrar a qué compañía pertenece cada miembro sin tener que
        // hacer lookup contra `users` después.
        company_id: user.company_id || companyId,
      };

      const participants = [currentParticipant, ...selectedUsers];

      const conversationId = await createConversation({
        type,
        participants,
        company_id: companyId,
        name: mode === "viaje"
          ? selectedTrip?.title || selectedTrip?.route?.name || selectedTrip?.id
          : type === "group" ? groupName || undefined : undefined,
        ...(mode === "viaje" && selectedTrip && {
          trip_id: selectedTrip.id,
          trip_name: selectedTrip.title || selectedTrip.route?.name || selectedTrip.id,
          route_id: selectedTrip.route_id || undefined,
        }),
      });

      onCreated(conversationId);
    } catch (error) {
      console.error("[NewConversation] Error creating:", error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className={`w-full max-w-md mx-4 rounded-2xl shadow-xl ${
          isDarkMode ? "bg-dark-surface" : "bg-white"
        }`}
      >
        {/* Header */}
        <div
          className={`px-5 py-4 border-b flex items-center justify-between ${
            isDarkMode ? "border-dark-border" : "border-gray-200"
          }`}
        >
          <h3
            className={`text-base font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {t("chat.newConversation")}
          </h3>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg ${
              isDarkMode ? "hover:bg-dark-hover text-gray-400" : "hover:bg-gray-100 text-gray-500"
            }`}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Type selector */}
          <div className="flex gap-2">
            <button
              onClick={() => { setMode("one-to-one"); setSelectedUsers([]); }}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                mode === "one-to-one"
                  ? "bg-primary-500 text-white"
                  : isDarkMode
                    ? "bg-dark-hover text-gray-300 hover:bg-dark-border"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <FontAwesomeIcon icon={faUser} className="text-xs" />
              {t("chat.directMessage")}
            </button>
            <button
              onClick={() => { setMode("group"); setSelectedUsers([]); }}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                mode === "group"
                  ? "bg-primary-500 text-white"
                  : isDarkMode
                    ? "bg-dark-hover text-gray-300 hover:bg-dark-border"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <FontAwesomeIcon icon={faUsers} className="text-xs" />
              {t("chat.groupChat")}
            </button>
            <button
              onClick={() => { setMode("viaje"); setSelectedUsers([]); }}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                mode === "viaje"
                  ? "bg-amber-500 text-white"
                  : isDarkMode
                    ? "bg-dark-hover text-gray-300 hover:bg-dark-border"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <FontAwesomeIcon icon={faTruck} className="text-xs" />
              {t("chat.tripChat")}
            </button>
          </div>

          {/* Group name */}
          {mode === "group" && (
            <input
              type="text"
              placeholder={t("chat.groupNamePlaceholder")}
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg text-sm outline-none border ${
                isDarkMode
                  ? "bg-dark-hover border-dark-border text-white placeholder:text-gray-500"
                  : "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400"
              }`}
            />
          )}

          {/* Trip fields */}
          {mode === "viaje" && (
            <div className="space-y-2">
              {/* Trip selector */}
              <div className="relative">
                <select
                  value={selectedTrip?.id || ""}
                  disabled={isLoadingTrips || isLoadingInitialTrip}
                  onChange={(e) => {
                    const trip = filteredTrips.find((tr) => tr.id === e.target.value) || null;
                    setSelectedTrip(trip);
                  }}
                  className={`w-full px-3 py-2 rounded-lg text-sm outline-none border appearance-none ${
                    isDarkMode
                      ? "bg-dark-hover border-dark-border text-white"
                      : "bg-gray-50 border-gray-200 text-gray-900"
                  } ${!selectedTrip ? (isDarkMode ? "text-gray-500" : "text-gray-400") : ""} ${isLoadingTrips || isLoadingInitialTrip ? "opacity-50" : ""}`}
                >
                  <option value="" disabled>
                    {isLoadingTrips || isLoadingInitialTrip ? t("chat.loadingTrips") : t("chat.tripIdPlaceholder")}
                  </option>
                  {filteredTrips.map((trip) => (
                    <option key={trip.id} value={trip.id}>
                      {trip.title || trip.id}{trip.route?.name ? ` — ${trip.route.name}` : ""}
                    </option>
                  ))}
                </select>
                {(isLoadingTrips || isLoadingInitialTrip) && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400 text-sm" />
                  </div>
                )}
              </div>

              {/* Selected trip info card */}
              {selectedTrip && (
                <div className={`rounded-lg border p-3 space-y-2 ${
                  isDarkMode ? "bg-dark-hover border-dark-border" : "bg-amber-50 border-amber-200"
                }`}>
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faTruck} className={`text-xs ${isDarkMode ? "text-amber-400" : "text-amber-600"}`} />
                    <span className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                      {selectedTrip.title || selectedTrip.id}
                    </span>
                    {selectedTrip.status?.name && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        isDarkMode ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700"
                      }`}>
                        {selectedTrip.status.name}
                      </span>
                    )}
                  </div>
                  {selectedTrip.description && (
                    <p className={`text-xs leading-relaxed ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                      <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                      {selectedTrip.description}
                    </p>
                  )}
                  {selectedTrip.route?.name && (
                    <div className={`flex items-center gap-1.5 text-xs ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}>
                      <FontAwesomeIcon icon={faRoute} className="text-[10px]" />
                      <span className="font-medium">{selectedTrip.route.name}</span>
                      {selectedTrip.route.description && (
                        <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>— {selectedTrip.route.description}</span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Selected users badges */}
          {(selectedUsers.length > 0 || (isGroupLike && hostParticipant)) && (
            <div className="space-y-2">
              <p className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                {t("chat.selectedUsers")} ({selectedUsers.length + (isGroupLike && hostParticipant ? 1 : 0)})
              </p>
              <div className="flex flex-wrap gap-2">
                {isGroupLike && hostParticipant && (
                  <span
                    key={hostParticipant.uid}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                      isDarkMode
                        ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                        : "bg-amber-50 text-amber-700 border border-amber-200"
                    }`}
                    title={t("chat.you")}
                  >
                    <FontAwesomeIcon icon={faUser} className="text-[10px]" />
                    {hostParticipant.display_name}
                    <span className="opacity-70">({t("chat.you")})</span>
                  </span>
                )}
                {selectedUsers.map((participant) => (
                  <span
                    key={participant.uid}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                      isDarkMode
                        ? "bg-primary-500/20 text-primary-300 border border-primary-500/30"
                        : "bg-primary-50 text-primary-700 border border-primary-200"
                    }`}
                  >
                    <FontAwesomeIcon icon={faUser} className="text-[10px]" />
                    {participant.display_name}
                    <button
                      type="button"
                      onClick={() => toggleUser(participant)}
                      className={`ml-0.5 hover:text-red-500 transition-colors`}
                    >
                      <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Search */}
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
              isDarkMode ? "bg-dark-hover" : "bg-gray-100"
            }`}
          >
            <FontAwesomeIcon
              icon={faSearch}
              className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            />
            <input
              type="text"
              placeholder={t("chat.searchUsers")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`flex-1 bg-transparent text-sm outline-none ${
                isDarkMode ? "text-white placeholder:text-gray-500" : "text-gray-900 placeholder:text-gray-400"
              }`}
            />
          </div>

          {/* Users list */}
          <div className="max-h-48 overflow-y-auto space-y-1">
            {isLoadingUsers && (
              <div className="flex items-center justify-center py-4 gap-2">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400" />
                <span className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                  {t("chat.loadingUsers")}
                </span>
              </div>
            )}
            {!isLoadingUsers && filteredUsers.length === 0 && (
              <p className={`text-center py-4 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                {debouncedSearch.length >= 2 ? t("chat.noUsersFound") : t("chat.noUsersAvailable")}
              </p>
            )}
            {!isLoadingUsers && filteredUsers.map((participant) => {
              const isSelected = selectedUsers.some((p) => p.uid === participant.uid);
              // Solo aplica en modo directo: si ya existe un 1:1 con este
              // usuario, lo bloqueamos y mostramos un badge informativo.
              const hasExistingDirect =
                mode === "one-to-one" &&
                existingDirectChatUserIds.has(participant.uid);
              return (
                <button
                  key={participant.uid}
                  onClick={() => toggleUser(participant)}
                  disabled={hasExistingDirect}
                  title={hasExistingDirect ? t("chat.alreadyHasDirectChat") : undefined}
                  className={`w-full px-3 py-2.5 rounded-lg flex items-center gap-3 transition-colors ${
                    hasExistingDirect
                      ? "opacity-50 cursor-not-allowed"
                      : isSelected
                        ? "bg-primary-50 dark:bg-primary-500/10 border border-primary-200 dark:border-primary-500/30"
                        : isDarkMode
                          ? "hover:bg-dark-hover"
                          : "hover:bg-gray-50"
                  }`}
                >
                  <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                    <FontAwesomeIcon icon={faUser} className="text-white text-xs" />
                  </div>
                  <div className="flex-1 text-left">
                    <p
                      className={`text-sm font-medium ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {participant.display_name}
                    </p>
                    {participant.email && (
                      <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                        {participant.email}
                      </p>
                    )}
                  </div>
                  {hasExistingDirect ? (
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        isDarkMode
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-200 text-gray-600"
                      }`}
                    >
                      {t("chat.alreadyHasDirectChatBadge")}
                    </span>
                  ) : isSelected ? (
                    <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div
          className={`px-5 py-4 border-t flex justify-end gap-2 ${
            isDarkMode ? "border-dark-border" : "border-gray-200"
          }`}
        >
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              isDarkMode
                ? "text-gray-300 hover:bg-dark-hover"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            {t("chat.cancel")}
          </button>
          <button
            onClick={handleCreate}
            disabled={selectedUsers.length === 0 || isCreating}
            className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
              selectedUsers.length > 0
                ? "bg-primary-500 hover:bg-primary-600"
                : "bg-gray-300 cursor-not-allowed"
            }`}
          >
            {isCreating ? (
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            ) : (
              t("chat.startChat")
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
